import React, { useState, useRef, useCallback } from 'react';
import {
    ChevronDown, Shield, LayoutDashboard, Settings,
    PlusCircle, Target, Users, Brain, X,
    Briefcase, Check, Search, FileCog,
    ShieldCheck, FileText, NotebookText, Sparkles, TriangleAlert,
    Building2
} from 'lucide-react';

import Logo from '../images/logo.png';
import LogoSM from '../images/logo_sm.png';
import { RoutesLink } from '../ApiHelper/RoutesLink';
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { switchProject } from '../redux/features/project/projectSlice';


function NavItem({ icon, label, active, expanded, onClick }) {
    return (
        <div className="relative group">
            <button
                onClick={onClick}
                className={`w-full flex items-center transition-all duration-200 rounded-xl font-bold 
                    ${active ? 'active text-white ' : 'text-slate-300 hover:bg-slate-800/60 hover:text-slate-200'}
                    ${expanded ? 'px-4 py-3 gap-3' : 'p-3 justify-center'}`}
            >
                <div className="shrink-0">{icon}</div>
                {expanded && <span className="text-xs tracking-widest truncate animate-in fade-in">{label}</span>}
            </button>
            {!expanded && (
                <div className="absolute left-full ml-4 px-3 py-2 bg-slate-900 text-white text-[10px] font-bold uppercase tracking-widest rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-[60] whitespace-nowrap shadow-xl border border-slate-700">
                    {label}
                </div>
            )}
        </div>
    );
}

const Sidebar = ({  projects, selectedProject, setSelectedProject, isSidebarOpen, setIsSidebarOpen, activeNav, setActiveNav, onNewProjectClick }) => {
    const navigate = useNavigate();
    // UI Interaction States
    const [isProjectSelectorOpen, setIsProjectSelectorOpen] = useState(false);
    const [projectSearchQuery, setProjectSearchQuery] = useState("");
    const projectSelectorRef = useRef(null);

    const dispatch = useDispatch();

    const filteredProjects = (projects || []).filter(p =>
        p.name.toLowerCase().includes(projectSearchQuery.toLowerCase())
    );

    const handleProjectChange = useCallback((project) => {
        dispatch(switchProject(project));
    }, [dispatch]);

    return (
        <aside
            className={`sidebar fixed inset-y-0 left-0 z-50  transition-all duration-300 ease-in-out lg:relative 
            ${isSidebarOpen ? 'w-64 translate-x-0' : 'w-20 lg:translate-x-0 -translate-x-full'}`}
        >
            <div className="flex flex-col h-full overflow-hidden">
                {/* Brand & Collapse Toggle */}
                <div className="flex items-center justify-between px-4 border-slate-200 flex-shrink-0 sidebar-logo" style={{ height: "80px" }}>
                    <div className="flex items-center flex-col">
                       { 
                        isSidebarOpen?<img src={Logo} alt="Tecorelabs Logo" />:
                        <img src={LogoSM} alt="Tecorelabs Logo" />
                       }
                        {/* <div className='flex items-center mt-2' style={{ alignSelf: "flex-end" }}>
                            <div className="w-6 h-6 bg-purple-600 rounded-lg flex items-center justify-center">
                                <Target className="h-4 w-4 text-purple-100" />
                            </div>
                            <h1 className="ml-2 text-md font-bold text-white">Compvault AI</h1>
                        </div> */}
                    </div>
                    <button className="lg:hidden" onClick={() => setIsSidebarOpen(false)}>
                        <X className="w-6 h-6 text-slate-500" />
                    </button>
                </div>

                {/* Searchable Project Selector */}
               {isSidebarOpen &&  <div className="px-4 py-2  relative" ref={projectSelectorRef}>
                    <button
                        onClick={() => isSidebarOpen && setIsProjectSelectorOpen(!isProjectSelectorOpen)}
                        className={`w-full flex items-center transition-all duration-200 border border-slate-800/20 rounded-xl group 
                            ${isSidebarOpen ? 'px-4 py-3 justify-between bg-slate-800/20 hover:bg-slate-800/30' : 'p-3 justify-center bg-indigo-600 '}`}
                    >
                        <div className="flex items-center gap-3 overflow-hidden">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors 
                                ${isSidebarOpen ? 'bg-indigo-500/10 text-indigo-400' : 'text-white'}`}>
                                <Briefcase size={18} />
                            </div>
                            {isSidebarOpen && (
                                <div className="text-left overflow-hidden">
                                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-none mb-1">Active Project</p>
                                    <p className="text-slate-200 text-sm font-semibold truncate">{selectedProject?.name || 'Select Project'}</p>
                                </div>
                            )}
                        </div>
                        {isSidebarOpen && <ChevronDown size={16} className={`text-slate-500 transition-transform ${isProjectSelectorOpen ? 'rotate-180' : ''}`} />}
                    </button>

                    {isProjectSelectorOpen && isSidebarOpen && (
                        <div className="absolute left-4 right-4 top-full mt-2 bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl z-[100] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                            <div className="p-3 border-b border-slate-700">
                                <div className="flex items-center bg-slate-900 px-3 py-2 rounded-lg border border-slate-700 focus-within:border-indigo-500 transition-colors">
                                    <Search size={14} className="text-slate-500" />
                                    <input
                                        autoFocus
                                        type="text"
                                        placeholder="Search projects..."
                                        value={projectSearchQuery}
                                        onChange={(e) => setProjectSearchQuery(e.target.value)}
                                        className="bg-transparent border-none outline-none text-xs text-white ml-2 w-full placeholder:text-slate-600"
                                    />
                                </div>
                            </div>
                            <div className="max-h-60 overflow-y-auto custom-scrollbar p-1">
                                {filteredProjects.map((p, index) => {
                                    // if(![
                                    //     "TecoreLabs Compliant SOC2 and HIPAA", 
                                    //     "Bank Audit 2025-26",
                                    //     "Infrastructure & Data",
                                    //     "online banking test",
                                    //     "Audit IT Jan2026",
                                    //     "TecoreLabs Audit Jan 2026"
                                    // ].includes(p?.name)) return;
                                    // Ensure we are checking against the correct ID property (projectId or id)
                                    const currentId = p.projectId || p.id;
                                    const activeId = selectedProject?.projectId || selectedProject?.id;
                                    const isSelected = currentId && activeId === currentId;

                                    return (
                                        <button
                                            key={currentId || index}
                                            onClick={() => {
                                                setSelectedProject(p);
                                                handleProjectChange(p);
                                                // localStorage.setItem('selectedProject', activeId);
                                                setIsProjectSelectorOpen(false);
                                                setProjectSearchQuery("");
                                                // Optionally set active nav to dashboard if needed when a project changes
                                                if (activeNav !== 'dashboard' && activeNav !== 'controls' && activeNav !== 'reports' && activeNav !== 'team') {
                                                    setActiveNav('dashboard');
                                                }
                                            }}
                                            className={`w-full mb-1 flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-medium transition-all
                                                ${isSelected ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-slate-700 hover:text-slate-200'}`}
                                        >
                                            <span className="truncate">{p.name}</span>
                                            {isSelected && <Check size={14} />}
                                        </button>
                                    )
                                })}
                                {filteredProjects.length === 0 && (
                                    <p className="px-3 py-4 text-xs text-center text-slate-500 italic">No projects found</p>
                                )}
                            </div>
                            <div className="p-2 bg-slate-900/50 border-t border-slate-700">
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onNewProjectClick();
                                        setIsProjectSelectorOpen(false);
                                    }}
                                    className="w-full flex items-center justify-center gap-2 py-2 text-[10px] font-bold text-indigo-400 hover:text-indigo-300 uppercase tracking-widest transition-colors"
                                >
                                    <PlusCircle size={14} />
                                    Create New Project
                                </button>
                            </div>
                        </div>
                    )}
                </div>}

                {/* Nav Links */}
                <nav className="flex-1 px-4 space-y-1 overflow-y-auto pt-4 pb-4 custom-sidebar-menu">
                    <NavItem icon={<Sparkles size={16} />} label="Arc AI" active={activeNav === 'ai_buddy'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.aiBuddy)} />
                    {/* <button class="gradient-button"  active={activeNav === 'ai_buddy'} expanded={isSidebarOpen} onClick={() => setActiveNav('ai_buddy')}>
                        <span class="gradient-text"> <Brain size={16} /> Ai Buddy</span>
                    </button> */}
                    {isSidebarOpen && (
                        <div className="pt-1 pb-2 px-4 animate-in fade-in">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Project Menus</span>
                        </div>
                    )}
                    <NavItem icon={<LayoutDashboard size={16} />} label="Dashboard" active={activeNav === 'dashboard'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.dashboard)} />
                    <NavItem icon={<Target size={16} />} label="Controls" active={activeNav === 'controls'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.controls)} />
                    <NavItem icon={<TriangleAlert size={16} />} label="Risks" active={activeNav === 'risks_page'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.risks)} />
                    <NavItem icon={<NotebookText size={16} />} label="Policy" active={activeNav === 'policy'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.policy)} />
                    <NavItem icon={<FileText size={16} />} label="Reports" active={activeNav === 'reports'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.reports)} />
                    <NavItem icon={<Users size={16} />} label="Team Member" active={activeNav === 'teamMember'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.teamMember)} />

                    {isSidebarOpen && (
                        <div className="pt-6 pb-2 px-4 animate-in fade-in">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Others</span>
                        </div>
                    )}

                    <NavItem icon={<FileCog size={16} />} label="Frameworks & Controls" active={activeNav === 'frameworks'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.frameworks)} />
                    <NavItem icon={<ShieldCheck size={16} />} label="Roles & Permission" active={activeNav === 'roles_permission'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.rolesPermission)} />
                    <NavItem icon={<LayoutDashboard size={16} />} label="Projects" active={activeNav === 'project'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.projects)} />
                    <NavItem icon={<Users size={16} />} label="Team Members" active={activeNav === 'team'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.team)} />
                
                    <NavItem icon={<Building2 size={18} />} label="Organizations" active={activeNav === 'organization'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.organization)} />
                
                    <NavItem icon={<Settings size={16} />} label="Settings" active={activeNav === 'settings'} expanded={isSidebarOpen} onClick={() => navigate(RoutesLink.settings)} />
                </nav>


            </div>
        </aside>
    );
};
export default Sidebar;